// 管理画面の一覧で、Excelのような列ごとのソート・フィルターを行うためのmixin
// - フィルター・並び順・ページ・表示件数は sessionStorage に保存し、別画面から戻っても復元する
// - 使う側は data に listItems（全件）を、methods/computed に filterColumns を用意する
//   filterColumns: [{ value: 列名, type: 'select' | 'text' | 'date', label: item => 表示・絞り込み用の文字列 }]
//   列ごとのフィルターの値は、select: 除外する値の配列 / text: 含む文字列 / date: { from, to }（YYYY-MM-DD）

export const BLANK_LABEL = '（空白）'

export const isFilterActive = (type, filter) => {
  if (!filter) return false
  if (type === 'select') return filter.length > 0
  if (type === 'text') return filter.trim() !== ''
  if (type === 'date') return !!(filter.from || filter.to)
  return false
}

const matchesFilter = (type, filter, label) => {
  if (!isFilterActive(type, filter)) return true
  const value = label == null || label === '' ? BLANK_LABEL : String(label)
  if (type === 'select') return !filter.includes(value)
  if (type === 'text') return value.toLowerCase().includes(filter.trim().toLowerCase())
  if (type === 'date') {
    const date = value.slice(0, 10)
    if (filter.from && date < filter.from) return false
    if (filter.to && date > filter.to) return false
    return true
  }
  return true
}

export default function columnFilterList(storageKey) {
  return {
    data() {
      return {
        listItems: [],
        columnFilters: {},
        sortBy: [],
        sortDesc: [],
        tablePage: 1,
        itemsPerPage: 50,
      }
    },

    created() {
      this.restoreListState()
    },

    watch: {
      columnFilters: { handler() { this.saveListState() }, deep: true },
      sortBy() { this.saveListState() },
      sortDesc() { this.saveListState() },
      tablePage() { this.saveListState() },
      itemsPerPage() { this.saveListState() },
    },

    computed: {
      filteredItems() {
        const columns = this.filterColumns.filter(column => isFilterActive(column.type, this.columnFilters[column.value]))
        if (!columns.length) return this.listItems
        return this.listItems.filter(item =>
          columns.every(column => matchesFilter(column.type, this.columnFilters[column.value], column.label(item)))
        )
      },
      activeFilterCount() {
        return this.filterColumns.filter(column => isFilterActive(column.type, this.columnFilters[column.value])).length
      },
    },

    methods: {
      // 列の見出しメニューから受け取ったフィルターを反映する（絞り込みが変わったら1ページ目に戻す）
      setColumnFilter(value, filter) {
        this.$set(this.columnFilters, value, filter)
        this.tablePage = 1
      },
      setColumnSort(value, direction) {
        if (!direction) {
          this.sortBy = []
          this.sortDesc = []
          return
        }
        this.sortBy = [value]
        this.sortDesc = [direction === 'desc']
      },
      columnSortDirection(value) {
        if (this.sortBy[0] !== value) return null
        return this.sortDesc[0] ? 'desc' : 'asc'
      },
      // 一覧を読み込んだあとに呼び、保存していたページに戻す
      applyRestoredPage() {
        if (!this.restoredPage) return
        const page = this.restoredPage
        this.restoredPage = null
        this.$nextTick(() => { this.tablePage = page })
      },
      clearAllFilters() {
        this.columnFilters = {}
        this.sortBy = []
        this.sortDesc = []
        this.tablePage = 1
      },
      saveListState() {
        const state = {
          columnFilters: this.columnFilters,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          tablePage: this.tablePage,
          itemsPerPage: this.itemsPerPage,
        }
        sessionStorage.setItem(storageKey, JSON.stringify(state))
      },
      restoreListState() {
        const saved = sessionStorage.getItem(storageKey)
        if (!saved) return
        try {
          const state = JSON.parse(saved)
          this.columnFilters = state.columnFilters || {}
          this.sortBy = state.sortBy || []
          this.sortDesc = state.sortDesc || []
          this.itemsPerPage = state.itemsPerPage || 50
          // 一覧の読み込み前は表がページを1に戻してしまうため、読み込み後に applyRestoredPage で戻す
          this.restoredPage = state.tablePage || 1
        } catch (e) {
          sessionStorage.removeItem(storageKey)
        }
      },
    },
  }
}
